import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';

function VerifyCode({ email, isLoading, error, onSubmit, onBack, onResend }) {
  // Use an array to hold the value of each of the 6 OTP input boxes
  const [otp, setOtp] = useState(new Array(6).fill(""));
  // Stable, non-index keys for the fixed-length OTP boxes (avoids S6479)
  const otpBoxIds = useRef(['otp-box-0', 'otp-box-1', 'otp-box-2', 'otp-box-3', 'otp-box-4', 'otp-box-5']);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState('');

  // Create an array of refs to hold references to each input element
  const inputRefs = useRef([]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const combinedOtp = otp.join('');
    // Ensure all 6 digits are filled before submitting
    if (combinedOtp.length === 6) {
      onSubmit(combinedOtp);
    }
  };

  const handleResendClick = async () => {
    setIsResending(true);
    setResendSuccess('');
    const success = await onResend();
    if (success) {
        setResendSuccess('A new code has been sent.');
        setResendCooldown(30); // Start 30-second cooldown
        setTimeout(() => setResendSuccess(''), 5000); // Clear message after 5 seconds
    }
    setIsResending(false);
  };

  // Handles input changes, overwriting, and automatic focus shifting
  const handleChange = (element, index) => {
    // Get the typed value and convert to uppercase for consistency
    const value = element.value.toUpperCase();

    // Overwrite the value at the current index
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character typed
    setOtp(newOtp);

    // Focus the next input box if a value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handles backspace key to move to the previous input
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Handles pasting an alphanumeric code into the inputs
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .slice(0, 6)
      .toUpperCase()
      .split('');
    
    const newOtp = [...otp];
    pastedData.forEach((char, index) => {
      newOtp[index] = char;
    });
    setOtp(newOtp);
    inputRefs.current[pastedData.length - 1]?.focus();
  };

  return (
    <Card style={{ width: '40em' }} className="p-4 shadow-sm questionnaire-card">
      <Card.Body>
        <Card.Title className="questionnaire-card-title h3 mb-3">Verify Your Identity</Card.Title>
        <Card.Text className=" mb-text-muted">
          An access code was sent to <strong>{email}</strong>.
        </Card.Text>
        <Form onSubmit={handleSubmit} className="mt-1">
          <Form.Group className="mb-3" controlId="formAccessCode">
            <Form.Label className='card-form-title'>Access Code</Form.Label>
            {/* The wrapping div is now left-aligned */}
            <div className="d-flex gap-2" onPaste={handlePaste}>
              {otp.map((data, index) => {
                return (
                  <Form.Control
                    key={otpBoxIds.current[index]}
                    type="text"
                    value={data}
                    maxLength={1}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    required
                    style={{ width: '3rem', height: '3.5rem', textAlign: 'center', fontSize: '1.5rem' }}
                  />
                );
              })}
            </div>
          </Form.Group>

          {error && <Alert variant="danger">{error}</Alert>}
          {resendSuccess && <Alert variant="success">{resendSuccess}</Alert>}

          <div className="d-flex gap-2">
            <Button variant="primary" type="submit" disabled={isLoading || isResending || otp.join('').length !== 6}>
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" />
                  <span className="ms-2">Verifying...</span>
                </>
              ) : (
                'Verify Code'
              )}
            </Button>
            <Button 
                variant="secondary" 
                onClick={handleResendClick} 
                disabled={isLoading || isResending || resendCooldown > 0}
            >
                {isResending ? 'Sending...' : (resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code')}
            </Button>
          </div>
        </Form>
        <div className="mt-3">
          <Button variant="link" onClick={onBack}>
            &larr; Back to email entry
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

export default VerifyCode;